import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { SyncCashRegisterUpdates } from '@shared';
import { normalizeWireDates } from '@shared/helpers/normalize-wire-dates';
import { NativeServiceSyncStatus } from '@shared/models/native-service-syncable';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { CashRegisterStatus } from 'src/app/point-of-sale/enums/cash-register-status';
import { CashRegisterStatusLog } from 'src/app/point-of-sale/models/cash-register-status-log';
import { OnlineOfflineService } from '../online-offine.service';
import { AppSettingsService } from '../app-settings.service';
import { CashRegisterStatusLogReplicationService } from '../cash-register-status-log-replication.service';
import { SyncStatusManager } from './sync-status-manager.service';
import { classifySyncFailure } from './sync-failure';

/**
 * Pushes locally-stored cash register status logs (register open/close events)
 * to the Edara backend, sourced entirely from Firestore (independent of the
 * Desktop app).
 *
 * Each cycle reads the records still pending sync, claims them as `Syncing` to
 * guard against concurrent double-sends, posts them to the backend grouped by
 * cash register, then marks them `Synced` on success or rolls them back to
 * `UnSynced` for retry on failure. The run is non-blocking and self-coalescing,
 * so it is safe to invoke from login, reconnect, periodic and post-open/close
 * triggers.
 */
@Injectable({ providedIn: 'root' })
export class CashRegisterStatusLogSyncService {
  private readonly http = inject(HttpClient);
  private readonly onlineOfflineService = inject(OnlineOfflineService);
  private readonly statusLogService = inject(CashRegisterStatusLogReplicationService);
  private readonly syncStatusManager = inject(SyncStatusManager);

  private readonly edaraCoreBaseUrl: string;
  private readonly entityType = 'cashRegisterStatusLog';
  private isSyncing = false;

  constructor() {
    this.edaraCoreBaseUrl = this.normalizeBaseUrl(
      AppSettingsService.appSettings.edaraCoreApi.baseUrl
    );
  }

  /**
   * Push every unsynced status log to the backend. Non-blocking and
   * self-coalescing — safe to call from the periodic/login/online triggers.
   */
  async pushUnsyncedToServer(): Promise<void> {
    if (this.isSyncing || !this.onlineOfflineService.isOnline) return;

    this.isSyncing = true;
    try {
      const unsynced = await this.statusLogService.getUnsyncedAsync();
      // `Failed` records were rejected on an earlier cycle; they keep retrying
      // so a rejection the backend later accepts is never stranded.
      const failed = await this.statusLogService.getFailedAsync();
      const staleSyncing = await this.statusLogService.getStaleSyncingAsync();
      const pending = [...unsynced, ...failed, ...staleSyncing];
      if (pending.length === 0) return;

      // Claim the records so a concurrent cycle cannot double-send them.
      const claimed = await this.syncStatusManager.applyStatus(
        this.entityType,
        pending,
        ReplicationSyncStatus.Syncing
      );

      for (const [cashRegisterId, group] of this.groupByCashRegister(claimed)) {
        await this.syncGroup(cashRegisterId, group);
      }
    } catch (error) {
      console.error('[CashRegisterStatusLogSync] Push failed:', error);
      Sentry.captureException(error);
    } finally {
      this.isSyncing = false;
    }
  }

  private async syncGroup(
    cashRegisterId: string,
    statusLogs: CashRegisterStatusLog[]
  ): Promise<void> {
    const payload = new SyncCashRegisterUpdates();
    payload.id = cashRegisterId;
    payload.cashTransactionLogs = [];
    payload.cashRegisterStatusLogs = statusLogs.map(log => this.toStatusLogDto(log));

    try {
      await firstValueFrom(
        this.http.put(
          `${this.edaraCoreBaseUrl}/api/pos/cashregisters/sync/${cashRegisterId}`,
          payload
        )
      );
      await this.syncStatusManager.applyStatus(
        this.entityType,
        statusLogs,
        ReplicationSyncStatus.Synced
      );
    } catch (error: any) {
      // A backend rejection becomes `Failed` (with the reason); a request that
      // never reached the backend stays `UnSynced`. Both are retried next
      // cycle — localId idempotency makes a re-send safe even if the request
      // actually landed.
      const failure = classifySyncFailure(error);
      await this.syncStatusManager.applyStatus(
        this.entityType,
        statusLogs,
        failure.status,
        failure.message
      );
      console.error(
        `[CashRegisterStatusLogSync] Backend sync failed for cash register ${cashRegisterId}; marked ${failure.status}.`,
        error
      );
      Sentry.captureException(error);
    }
  }

  /**
   * Rebuild the backend wire shape from the Firestore record. `syncMetadata`
   * was stripped before persisting, so it is re-derived here from
   * `metadata.localId` (the same id the record replicated to the Desktop with),
   * onto the details block that matches the log's status — this is the backend
   * idempotency key.
   *
   * The finished shape is passed through {@link normalizeWireDates} because a
   * record restored from the Desktop stores its dates as `{seconds,
   * nanoseconds}`.
   */
  private toStatusLogDto(log: CashRegisterStatusLog): any {
    const syncMetadata = {
      localId: log.metadata!.localId,
      syncStatus: NativeServiceSyncStatus.Pending
    };
    // The backend keys the save on the ITEM-level cashRegisterId + syncMetadata
    // (the legacy Desktop body carried both); syncMetadata is also duplicated
    // inside the status-matching details block. `type` mirrors the legacy shape.
    const dto: any = {
      type: 'status_log',
      status: log.status,
      cashRegisterId: log.cashRegister?.id,
      changeDate: log.changeDate,
      changedBy: log.changedBy,
      openingDetails: log.openingDetails,
      closingDetails: log.closingDetails,
      syncMetadata
    };
    if (log.status === CashRegisterStatus.Closed) {
      dto.closingDetails = { ...(log.closingDetails ?? {}), syncMetadata };
    } else {
      dto.openingDetails = { ...(log.openingDetails ?? {}), syncMetadata };
    }
    return normalizeWireDates(dto);
  }

  private groupByCashRegister(
    statusLogs: CashRegisterStatusLog[]
  ): Map<string, CashRegisterStatusLog[]> {
    const groups = new Map<string, CashRegisterStatusLog[]>();
    for (const log of statusLogs) {
      const id = log.cashRegister?.id;
      if (!id) continue;
      const bucket = groups.get(id) ?? [];
      bucket.push(log);
      groups.set(id, bucket);
    }
    return groups;
  }

  private normalizeBaseUrl(url: string): string {
    return url?.endsWith('/') ? url.slice(0, -1) : url;
  }
}
